import type { LitElement } from 'lit';
import { localStorage_get, localStorage_set } from '../../../../../_ui-core_/base/package/util/localStorage';
import { LOCAL_STORAGE_KEY_VIEWED_STORIES } from '../../../../../_ui-core_/components/ui-stories/stories-constants';
import type { UiStoryViewer } from '../../../../../_ui-core_/components/ui-stories/ui-story-viewer/ui-story-viewer';
import type { InnerStoryItem, Story } from '../../cms/package/cms-domain';
import { LobbyStoriesEventAction, type LobbyStoriesTrackingParams } from '../tracking/tracking-service';

interface StoriesCb {
  onPresetLoaded: () => void;
  onClosedCb: (idx: number) => void;
}

function preloadImage(url: string) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
}

function preloadVideo(videoUrl: string, posterUrl: string) {
  fetch(videoUrl, { method: 'GET', mode: 'no-cors', cache: 'force-cache' }).catch((err) =>
    window.$app.logger.warn(`Could not prefetch next video asset: ${videoUrl}`, err),
  );
  if (posterUrl) {
    fetch(posterUrl, { method: 'GET', mode: 'no-cors', cache: 'force-cache' }).catch((err) =>
      window.$app.logger.warn(`Could not prefetch next video poster: ${posterUrl}`, err),
    );
  }
}

export function getStoryAssetType(stories: Story | Story[]): 'image' | 'video' | 'both' {
  const storyArray = Array.isArray(stories) ? stories : [stories];

  const items = storyArray.flatMap((story) => story?.innerStoryItems ?? []);
  const hasVideo = items.some((item) => !!item.storyDetailVideoPoster);
  const hasImage = items.some((item) => !item.storyDetailVideoPoster);

  if (hasVideo && hasImage) {
    return 'both';
  }

  if (hasVideo) {
    return 'video';
  }

  return 'image';
}

interface ViewedStoryEntry {
  id: string;
  count: number;
}

export function loadStoryViews(): Array<ViewedStoryEntry> {
  return localStorage_get(LOCAL_STORAGE_KEY_VIEWED_STORIES, []);
}

function saveStoryViews(viewedStories: Array<ViewedStoryEntry>) {
  localStorage_set(LOCAL_STORAGE_KEY_VIEWED_STORIES, viewedStories);
}

class StoryService {
  private _root: LitElement | ShadowRoot;
  private _storyViewer: any;
  private _activeStory?: UiStoryViewer;
  private _currentIdx: number | null = null;
  private _prevIdx: number | null = null;
  private _nextIdx: number | null = null;
  private _currentInnerIdx = 0;
  private _stories: Story[];
  private _callbacks: StoriesCb;
  private readonly _seenStoryIdsInViewer = new Set<string>();
  private readonly _fullyWatchedStoryIds = new Set<string>();
  private _isCurrentStoryMarkedComplete = false;

  private readonly _onNextStory = () => {
    if (this._currentStoryIndex === null) return;

    const currentStory = this._stories[this._currentStoryIndex];
    if (!currentStory) return;

    if (this._didFullyWatchCurrentStory() && !this._isCurrentStoryMarkedComplete) {
      this._fullyWatchedStoryIds.add(currentStory.id);
      this._isCurrentStoryMarkedComplete = true;
    }

    const hasMoreInnerItems = this._currentInnerIdx < currentStory.innerStoryItems.length - 1;

    if (hasMoreInnerItems) {
      this._currentInnerIdx++;
      this.show(this._currentStoryIndex);
      return;
    }

    if (this._nextIdx === null) {
      this._onCloseStory();
      return;
    }

    const nextStory = this._stories[this._nextIdx];
    if (!nextStory) {
      this._onCloseStory();
      return;
    }

    this._currentStoryIndex = this._nextIdx!;
    this._currentInnerIdx = 0;
    this._isCurrentStoryMarkedComplete = false;
    this.show(this._currentStoryIndex);
  };

  private readonly _onPrevStory = () => {
    if (this._currentStoryIndex === null) return;

    const currentStory = this._stories[this._currentStoryIndex];
    if (!currentStory) return;

    if (this._didFullyWatchCurrentStory() && !this._isCurrentStoryMarkedComplete) {
      this._fullyWatchedStoryIds.add(currentStory.id);
      this._isCurrentStoryMarkedComplete = true;
    }

    if (this._currentInnerIdx > 0) {
      this._currentInnerIdx--;
      this.show(this._currentStoryIndex);
      return;
    }

    if (this._prevIdx === null) {
      this.show(this._currentStoryIndex);
      return;
    }

    const prevStory = this._stories[this._prevIdx];
    if (!prevStory) {
      this.show(this._currentStoryIndex);
      return;
    }

    this._currentStoryIndex = this._prevIdx!;
    this._currentInnerIdx = Math.max(prevStory.innerStoryItems.length - 1, 0);
    this._isCurrentStoryMarkedComplete = false;
    this.show(this._currentStoryIndex);
  };

  private readonly _jumpToNextStory = () => {
    if (this._currentStoryIndex === null) return;

    const currentStory = this._stories[this._currentStoryIndex];
    if (!currentStory) return;

    if (this._didFullyWatchCurrentStory() && !this._isCurrentStoryMarkedComplete) {
      this._fullyWatchedStoryIds.add(currentStory.id);
      this._isCurrentStoryMarkedComplete = true;
    }

    if (this._nextIdx === null) {
      this._onCloseStory();
      return;
    }

    const nextStory = this._stories[this._nextIdx];
    if (!nextStory) {
      this._onCloseStory();
      return;
    }

    this._currentStoryIndex = this._nextIdx;
    this._currentInnerIdx = 0;
    this._isCurrentStoryMarkedComplete = false;

    this.show(this._currentStoryIndex);
  };

  private readonly _jumpToPrevStory = () => {
    if (this._currentStoryIndex === null) return;

    const currentStory = this._stories[this._currentStoryIndex];
    if (!currentStory) return;

    if (this._didFullyWatchCurrentStory() && !this._isCurrentStoryMarkedComplete) {
      this._fullyWatchedStoryIds.add(currentStory.id);
      this._isCurrentStoryMarkedComplete = true;
    }

    if (this._prevIdx === null) {
      this.show(this._currentStoryIndex);
      return;
    }

    const prevStory = this._stories[this._prevIdx];
    this._currentStoryIndex = this._prevIdx;
    this._currentInnerIdx = Math.max(prevStory!.innerStoryItems.length - 1, 0);
    this._isCurrentStoryMarkedComplete = false;
    this.show(this._currentStoryIndex);
  };

  private readonly _onCloseStory = () => {
    if (this._didFullyWatchCurrentStory() && !this._isCurrentStoryMarkedComplete) {
      const currentStory = this._stories[this._currentStoryIndex!];
      if (currentStory) {
        this._fullyWatchedStoryIds.add(currentStory.id);
      }
    }

    const fullyWatchedCount = this._fullyWatchedStoryIds.size;
    if (fullyWatchedCount > 0) {
      const ids = Array.from(this._fullyWatchedStoryIds).join(' | ');

      window.$app.track.lobbyStories({
        eventAction: LobbyStoriesEventAction.TOTAL_SEEN,
        fullyWatched: fullyWatchedCount,
        storyID: ids,
      });
    }
    this._callbacks.onClosedCb(this._currentStoryIndex!);
    this.closeAll();
  };

  private readonly _onKeyDown = (e: KeyboardEvent) => {
    e.preventDefault();

    if (e.key === 'Escape') {
      this._activeStory?.close();
      return;
    }

    if (e.key === 'ArrowRight') {
      this._onNextStory();
      return;
    }

    if (e.key === 'ArrowLeft') {
      this._onPrevStory();
    }
  };

  public async startView(stories: Story[], startingIdx: number, callbacks: StoriesCb) {
    if (!this._storyViewer) {
      const module = await import('../../../../../_ui-core_/components/ui-stories/ui-story-viewer/ui-story-viewer');
      this._storyViewer = module.UiStoryViewer;
    }
    this._seenStoryIdsInViewer.clear();
    this._fullyWatchedStoryIds.clear();
    this._isCurrentStoryMarkedComplete = false;

    this._registerEventListeners();
    window.$app.modal.handleBodyScroll('lock', 'story');
    this._stories = stories;
    this._callbacks = callbacks;
    this._currentStoryIndex = startingIdx;
    this._currentInnerIdx = 0;

    const story = this._stories[startingIdx];
    if (!story) {
      window.$app.logger.error('Story does not exist', this._stories);
      this._currentStoryIndex = null;
      this.closeAll();
      return;
    }

    const inner = this._getInnerItem(startingIdx, this._currentInnerIdx);
    if (!inner) {
      window.$app.logger.warn('Story has no inner items', story);
      this._currentStoryIndex = null;
      this.closeAll();
      return;
    }

    const preloadAsset = inner.storyDetailVideoPoster || inner.storyAsset;
    if (!preloadAsset) {
      window.$app.logger.warn('Inner item missing asset', inner);
      this._callbacks.onPresetLoaded();
      this.show(this._currentStoryIndex, true);
      return;
    }

    preloadImage(preloadAsset).then(() => {
      this._callbacks.onPresetLoaded();
      this.show(this._currentStoryIndex, true);
    });
  }

  public show(idx: number | null, isFirstStory = false): void {
    if (idx === null) {
      this._activeStory?.close();
      return;
    }

    this.closeActiveStory();
    this._currentStoryIndex = idx;

    const story = this._stories[idx];
    const inner = this._getInnerItem(idx, this._currentInnerIdx);

    if (!story || !inner) {
      window.$app.logger.warn('Cannot show story or inner item', { story, inner });
      return;
    }

    window.$app.logger.log('show story', story, 'inner item', inner);

    this._activeStory = new this._storyViewer();
    if (!this._activeStory) return;

    this._activeStory.storyId = story.id;
    this._activeStory.isFirst = isFirstStory;
    this._activeStory.ctaLabel = inner.ctaLabel;
    this._activeStory.storyAsset = inner.storyAsset;
    if (inner.storyDetailVideoPoster) {
      this._activeStory.storyDetailVideoPoster = inner.storyDetailVideoPoster;
    }
    this._activeStory.storyName = story.title;
    this._activeStory.ctaAction = inner.ctaAction;
    if (story.campaignIds?.length) {
      this._activeStory.campaignIds = story.campaignIds;
    }
    this._activeStory.totalInnerItems = story.innerStoryItems.length;
    this._activeStory.currentInnerIndex = this._currentInnerIdx;
    this._activeStory.onStartCb = () => this._onStoryShownCb(story.uniqueId);
    this._activeStory.onCloseCb = this._onCloseStory;
    this._activeStory.onPrevCb = this._onPrevStory;
    this._activeStory.onSwipePrevCb = this._jumpToPrevStory;
    this._activeStory.onNextCb = this._onNextStory;
    this._activeStory.onSwipeNextCb = this._jumpToNextStory;
    this._getRoot().appendChild(this._activeStory);
  }

  public closeActiveStory() {
    if (this._activeStory) {
      this._activeStory.remove();
      this._activeStory = undefined;
    }
  }

  public closeAll(): void {
    this.closeActiveStory();
    this._removeEventListeners();
    window.$app.modal.handleBodyScroll('unlock', 'story');
  }

  private get _currentStoryIndex(): number | null {
    return this._currentIdx;
  }

  private set _currentStoryIndex(value) {
    this._currentIdx = value;
    if (value !== null) {
      this._prevIdx = value > 0 ? value - 1 : null;
      this._nextIdx = value < this._stories.length - 1 ? value + 1 : null;
    }
  }

  private _getInnerItem(storyIdx: number, innerIdx = 0): InnerStoryItem | null {
    const story = this._stories[storyIdx];
    if (!story) return null;
    const items = story.innerStoryItems ?? [];
    return items[innerIdx] ?? null;
  }

  private _registerEventListeners() {
    window.addEventListener('keydown', this._onKeyDown);
  }

  private _removeEventListeners() {
    window.removeEventListener('keydown', this._onKeyDown);
  }

  private _getRoot(): LitElement | ShadowRoot {
    if (!this._root) {
      this._root = document.querySelector('app-root') as LitElement;
      if (this._root === null) {
        window.$app.logger.warn('cannot select root element!');
        throw new Error('Cannot Select Root Element');
      }
      if (this._root.shadowRoot !== null) {
        this._root = this._root.shadowRoot;
      }
    }
    return this._root;
  }

  private _didFullyWatchCurrentStory(): boolean {
    if (this._currentStoryIndex === null) return false;
    const story = this._stories[this._currentStoryIndex];
    if (!story) return false;
    return this._currentInnerIdx === story.innerStoryItems.length - 1;
  }

  private _onStoryShownCb(storyUniqueId: string) {
    if (this._currentStoryIndex === null) return;

    const currentStory = this._stories[this._currentStoryIndex];
    if (!currentStory) return;

    if (!this._seenStoryIdsInViewer.has(currentStory.id)) {
      this._seenStoryIdsInViewer.add(currentStory.id);

      const trackingData: LobbyStoriesTrackingParams = {
        eventAction: LobbyStoriesEventAction.STORY_SEEN,
        storyID: currentStory.id,
        storyName: currentStory.title,
        assetType: getStoryAssetType(currentStory),
      };

      if (currentStory.campaignIds?.length) {
        trackingData.campaignID = currentStory.campaignIds.join(' | ');
      }

      window.$app.track.lobbyStories(trackingData);
    }

    const viewedStories = loadStoryViews();
    const entry = viewedStories.find((viewedStory) => viewedStory.id === storyUniqueId);

    if (entry && this._currentInnerIdx === 0) {
      entry.count++;
      saveStoryViews(viewedStories);

      window.$app.track.lobbyStories({
        eventAction: LobbyStoriesEventAction.STORY_REPEAT,
        storyID: currentStory.id,
        storyViewCount: entry.count,
        assetType: getStoryAssetType(currentStory),
      });
    }

    if (!entry) {
      viewedStories.push({ id: storyUniqueId, count: 1 });
      saveStoryViews(viewedStories);
    }

    const nextInnerIdx = this._currentInnerIdx + 1;
    let nextInner: InnerStoryItem | null = this._getInnerItem(this._currentStoryIndex, nextInnerIdx);

    if (!nextInner && this._nextIdx !== null) {
      nextInner = this._getInnerItem(this._nextIdx, 0);
    }

    if (!nextInner) return;

    const nextAsset = nextInner.storyAsset;
    const nextPoster = nextInner.storyDetailVideoPoster;

    if (nextPoster) {
      preloadVideo(nextAsset, nextPoster);
    } else {
      preloadImage(nextAsset);
    }
  }
}

export const storyService = new StoryService();
